/* eslint-disable indent */
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'layout';
import { COUNCIL_TYPE_CODE, TOAST, sameProjectType } from 'constant';

const useSelectionProposal = (
  _data,
  pagination,
  curMembers,
  councilId,
  councilTypeSelected,
  setFilterByProgram,
  isSelectOne,
  isChangeSelectionType = false
) => {
  // eslint-disable-next-line no-unused-vars
  const { t } = useTranslation();
  const data = useMemo(
    () => _data?.data?.filter((item) => item.deletedAt === null) ?? [],
    [_data?.data]
  );
  // Case 1: isSelectedAll = true
  //      => selectedItems save all item in current page (for UI)
  // Case 2: isSelectedAll = false & isUnselectedSome = true
  //         (happen when isSelectedAll true => false)
  //      => unselectedItems save items that user unselect (for filter out)
  // Case 3: isSelectedAll = false & isUnselectedSome = false & selectedItems has any
  //         (happen when user select any item)
  //      => selectedItems save items that user select (for filter)
  // Case 4: isSelectedAll = false & isUnselectedSome = false & selectedItems don't has any
  //         (happen when user don't select any item)
  //      => default value of 4 these state

  const [isSelectedAll, setIsSelectedAll] = useState(false);
  // Flag unselectedSome is true when isSelectedAll change from true to false
  const [isUnselectedSome, setIsUnselectedSome] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  // Save items are unselected
  const [unselectedItems, setUnselectedItems] = useState([]);

  // Catch error
  // eslint-disable-next-line no-unused-vars
  const [isError, setIsError] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [messageError, setMessageError] = useState('');

  useEffect(() => {
    if (!isChangeSelectionType) {
      if (data?.length) {
        if (isSelectedAll) {
          const isSameProgram = data.some((item) => item.programId !== data[0].programId);
          if (!isSameProgram) {
            setIsError(true);
            setMessageError('You can only select items in the same program');
            toast(TOAST.ERROR, 'Các đề tài phải cùng một chương trình');
            return;
          }
          // Case 1
          setSelectedItems(data);
        } else if (isUnselectedSome) {
          // Case 2
          const newSelectedItems = data.filter((item) => {
            const index = unselectedItems.findIndex(
              (unselectedItem) => unselectedItem.reference === item.reference
            );
            return index === -1;
          });
          setSelectedItems(newSelectedItems);
        } else if (!isUnselectedSome) {
          // Case 3
          const newUnselectedItems = data.filter(
            (item) =>
              selectedItems.findIndex(
                (selectedItem) => selectedItem.reference === item.reference
              ) === -1
          );
          setUnselectedItems(newUnselectedItems);
        }
      }
    }
  }, [pagination, data]);

  const handleAllRowSelect = () => {
    if (data.length) {
      // check same projectType
      // const isSameProjectType = data.some((item) => item.projectType !== data[0].projectType);
      // if (isSameProjectType) {
      //   setIsError(true);
      //   setMessageError('You can only select items in the same project type');
      //   toast(TOAST.ERROR, t('errorMessage.projectProposalsMustBelongToTheSameProjectType'));
      //   return;
      // }
      // // check same program
      // const isSameProgram = data.some((item) => item.programId !== data[0].programId);
      // if (isSameProgram) {
      //   setIsError(true);
      //   setMessageError('You can only select items in the same program');
      //   toast(TOAST.ERROR, 'Các đề tài phải cùng một chương trình');
      //   return;
      // }

      // check member council
      const isExistedMemberInProjectProposal = data.some((projectProposal) =>
        curMembers.some((member) =>
          projectProposal?.projectProposalContacts.find((contact) => contact.email === member.email)
        )
      );
      if (isExistedMemberInProjectProposal) {
        toast(
          TOAST.ERROR,
          `${t('errorMessage.councilMember')} ${t('errorMessage.relatedToThisProjectProposal')}`
        );
        return;
      }

      // check project type
      // const isSameProjectType = sameProjectType.some((projectType) => {
      //   if (data.array.forEach(element => {
      //     if (element.projectType !== projectType) {
      //       return false;
      //     }
      //   }))
      // })
      setIsSelectedAll(true);
      // Save all items in current page
      setSelectedItems(data);
    }
    setUnselectedItems([]);
  };

  const handleAllRowUnselect = () => {
    setIsSelectedAll(false);
    setIsUnselectedSome(false);
    const newSelectedItems = selectedItems.filter((item) => {
      if (item.hasCouncilResult || item.hasReview) {
        return true;
      }
      return false;
    });
    setSelectedItems(newSelectedItems);
    setUnselectedItems([]);
    setFilterByProgram([true, null]);
  };

  const handleRowSelect = (e) => {
    // Case 3
    // check same program
    if (isSelectOne) {
      if (
        councilTypeSelected?.code !== COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL &&
        selectedItems.length === 1
      ) {
        setIsError(true);
        setMessageError('Chỉ chọn duy nhất 1 đề tài cho hội đồng này');
        toast(TOAST.ERROR, 'Chỉ chọn duy nhất 1 đề tài cho hội đồng này');
        return;
      }
    }

    if (selectedItems.length > 0 && sameProjectType.includes(selectedItems[0]?.projectType)) {
      const isSameProjectType = selectedItems.some(
        (item) => item.projectType !== e.data.projectType
      );
      if (isSameProjectType) {
        setIsError(true);
        setMessageError('You can only select items in the same project type');
        toast(TOAST.WARN, t('errorMessage.projectProposalsMustBelongToTheSameProjectType'));
        return;
      }
      // const isSameProgram = selectedItems.some((item) => item.programId === e.data.programId);
      // if (!isSameProgram) {
      //   setIsError(true);
      //   setMessageError('You can only select items in the same program');
      //   toast(TOAST.ERROR, t('errorMessage.projectProposalsMustBelongToTheSameProgram'));
      //   return;
      // }
    }

    if (selectedItems.length > 0 && sameProjectType.includes(e.data.projectType)) {
      const isSameProjectType = selectedItems.some(
        (item) => item.projectType !== e.data.projectType
      );
      if (isSameProjectType) {
        toast(TOAST.WARN, 'Đề tài vừa chọn chỉ chấp nhận cùng một loại đề tài');
        return;
      }
    }

    // check qualified add for council
    // case: preliminarySelectionCouncil, check
    let isQualified = true;
    let messageError1 = '';
    const nProposal = e.data?.projectProposalCouncils?.length ?? 0;
    switch (councilTypeSelected?.code) {
      case 'preliminarySelectionCouncil':
        if (nProposal > 1) {
          isQualified = false;
          setMessageError('Đề tài này đã có hội đồng sơ tuyển');
          messageError1 = 'Đề tài này đã có hội đồng sơ tuyển';
        }
        if (nProposal === 1) {
          if (
            !councilId ||
            e.data.projectProposalCouncils.find(
              (item) => item.councilType.code === 'preliminarySelectionCouncil'
            ).councilId !== councilId
          ) {
            isQualified = false;
            setMessageError('Đề tài này đã có hội đồng sơ tuyển');
            messageError1 = 'Đề tài này đã có hội đồng sơ tuyển';
          }
        }
        break;
      case 'specializationCouncil':
        if (nProposal > 2) {
          isQualified = false;
          setMessageError('Đề tài này đã có hội đồng thẩm định chuyên môn');
          messageError1 = 'Đề tài này đã có hội đồng thẩm định chuyên môn';
        }
        if (nProposal === 0) {
          isQualified = false;
          setMessageError('Đề tài này chưa có hội đồng sơ tuyển');
          messageError1 = 'Đề tài này chưa có hội đồng sơ tuyển';
        }
        if (nProposal === 2) {
          if (
            !councilId ||
            e.data.projectProposalCouncils.find(
              (item) => item.councilType.code === 'specializationCouncil'
            ).councilId !== councilId
          ) {
            setMessageError('Đề tài này đã có hội đồng thẩm định chuyên môn');
            messageError1 = 'Đề tài này đã có hội đồng thẩm định chuyên môn';
            isQualified = false;
          }
        }
        if (nProposal === 1) {
          if (e.data.projectProposalCouncils[0].councilStatus !== 'qualified') {
            setMessageError('Đề tài này không đủ điều kiện tham gia hội đồng thẩm định chuyên môn');
            messageError1 = 'Đề tài này không đủ điều kiện tham gia hội đồng thẩm định chuyên môn';
            isQualified = false;
          }
        }
        break;
      case 'financeCouncil':
        if (nProposal > 3) {
          isQualified = false;
          setMessageError('Đề tài này đã có Tổ thẩm định kinh phí');
          messageError1 = 'Đề tài này đã có Tổ thẩm định kinh phí';
        }
        if (nProposal === 0) {
          isQualified = false;
          setMessageError('Đề tài này chưa có hội đồng sơ tuyển');
          messageError1 = 'Đề tài này chưa có hội đồng sơ tuyển';
        }
        if (nProposal === 1) {
          isQualified = false;
          setMessageError('Đề tài này chưa có hội đồng thẩm định chuyên môn');
          messageError1 = 'Đề tài này chưa có hội đồng thẩm định chuyên môn';
        }
        if (nProposal === 2) {
          if (
            e.data.projectProposalCouncils.find(
              (item) => item.councilType.code === 'specializationCouncil'
            ).councilStatus !== 'qualified'
          ) {
            setMessageError('Đề tài này không đủ điều kiện tham gia Tổ thẩm định kinh phí');
            messageError1 = 'Đề tài này không đủ điều kiện tham gia Tổ thẩm định kinh phí';
            isQualified = false;
          }
        }
        if (nProposal === 3) {
          if (
            !councilId ||
            e.data.projectProposalCouncils.find(
              (item) => item.councilType.code === 'financeCouncil'
            ).councilId !== councilId
          ) {
            isQualified = false;
            setMessageError('Đề tài này đã có Tổ thẩm định kinh phí');
            messageError1 = 'Đề tài này đã có Tổ thẩm định kinh phí';
          }
        }
        break;
      case 'midtermReportCouncil':
      case 'acceptanceCouncil':
        break;
      default:
        break;
    }
    // const isQualified = e.data.projectProposalCouncils.some((item) =>
    //   item.councilType.id === councilTypeSelected.id && item.councilStatus === 'qualified');
    if (!isQualified) {
      setIsError(true);
      toast(TOAST.ERROR, messageError1);
      return;
    }

    // check have members in council
    // const isExistedMemberInProjectProposal = curMembers.some(member =>
    //   e.data.projectProposalContacts.find(contact => contact.email === member.email));
    const membersExistedInProjectProposal = curMembers.filter((member) =>
      e.data.projectProposalContacts.find((contact) => contact.email === member.email)
    );
    if (membersExistedInProjectProposal.length > 0) {
      toast(
        TOAST.ERROR,
        `${t('errorMessage.councilMember')} (${membersExistedInProjectProposal
          .map((member) => member.email)
          .join(',')}) ${t('errorMessage.relatedToThisProjectProposal')}`
      );
      return;
    }

    // check number of project proposal in council
    if (
      councilTypeSelected.code !== 'financeCouncil' &&
      councilTypeSelected.code !== 'preliminarySelectionCouncil'
    ) {
      if (selectedItems.length >= 1) {
        toast(TOAST.ERROR, t('errorMessage.councilOnlyHaveOneProjectProposal'));
        return;
      }
    }

    // set filter
    if (selectedItems.length === 0) {
      setFilterByProgram([true, e.data.programId]);
    }

    const newSelectedItems = [...selectedItems, e.data];
    setSelectedItems(newSelectedItems);

    // Case 2
    // remove unselecteditem
    const newUnselectedItem = unselectedItems.filter((item) => item.reference !== e.data.reference);
    if (newUnselectedItem.length === 0) {
      // If all unselected items are removed => select all
      if (isUnselectedSome || newSelectedItems.length === _data.pagination.totalRecords) {
        setIsSelectedAll(true);
        setIsUnselectedSome(false);
      }
    }
    setUnselectedItems(newUnselectedItem);
    setIsError(false);
  };

  const handleRowUnselect = (e) => {
    if (isSelectedAll || isUnselectedSome) {
      // Case 3
      const newUnselectedItems = [...unselectedItems, e.data];
      if (newUnselectedItems.length === _data.pagination.totalRecords) {
        setIsSelectedAll(false);
        setIsUnselectedSome(false);
        setUnselectedItems([]);
      } else {
        // Case 2
        setIsSelectedAll(false);
        setIsUnselectedSome(true);
        setUnselectedItems((cur) => [...cur, e.data]);
      }
    }

    // check when council has council result or review by member
    if (e.data?.hasCouncilResult) {
      toast(TOAST.ERROR, t('errorMessage.projectProposalHasResult'));
      return;
    }
    if (e.data?.hasReview) {
      toast(TOAST.ERROR, t('errorMessage.projectProposalHasReview'));
      return;
    }

    // remove selectedItem
    const newSelectedItems = selectedItems.filter((item) => item.reference !== e.data.reference);
    setSelectedItems(newSelectedItems);
    if (newSelectedItems.length === 0) {
      setFilterByProgram([true, null]);
    }
  };

  return {
    selectedItems,
    unselectedItems,
    isSelectedAll,
    isUnselectedSome,
    handleAllRowSelect,
    handleAllRowUnselect,
    handleRowSelect,
    handleRowUnselect,
    setSelectedItems,
    setIsSelectedAll,
    isError,
    messageError,
  };
};

export default useSelectionProposal;
